import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { usePortalAuth } from "../lib/portalAuth";
import { OW_API as ADMIN_API } from "../lib/operatorWalk";

function formatDate(iso) {
  if (!iso) return "Not set";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminBlogList({ onNew, onEdit }) {
  const { authHeader } = usePortalAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await axios.get(`${ADMIN_API}/admin/blog`, { headers: authHeader });
        setPosts(res.data?.posts || []);
      } catch {
        toast.error("Could not load posts");
      } finally {
        setLoading(false);
      }
    },
    [authHeader]
  );

  useEffect(() => {
    load();
  }, [load]);

  const deletePost = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${ADMIN_API}/admin/blog/${id}`, { headers: authHeader });
      setPosts((rows) => rows.filter((r) => r.id !== id));
      toast.success("Post deleted.");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  };

  return (
    <div data-testid="admin-blog-list">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <div className="eyebrow mb-1">Blog</div>
          <h2 className="font-serif text-3xl text-brand-700 leading-tight">
            All posts
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(true)}
            className="inline-flex items-center gap-2 text-sm text-brand-700 hover:bg-brand-100 px-3 py-2 transition-colors"
            data-testid="admin-blog-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={onNew}
            className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 text-sm font-medium transition-colors"
            data-testid="admin-blog-new-btn"
          >
            <Plus className="w-4 h-4" /> New Post
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-ink-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading
        </div>
      ) : posts.length === 0 ? (
        <div className="py-16 text-center border border-ink-line" data-testid="admin-blog-empty">
          <div className="font-serif text-2xl text-brand-700">No posts yet.</div>
          <p className="mt-3 text-ink-muted">Click "New Post" to write your first.</p>
        </div>
      ) : (
        <div className="border border-ink-line overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-blog-table">
            <thead>
              <tr className="bg-brand-50/50 border-b border-ink-line text-left">
                <th className="px-4 py-3 font-medium text-brand-700">Title</th>
                <th className="px-4 py-3 font-medium text-brand-700 w-32">Status</th>
                <th className="px-4 py-3 font-medium text-brand-700 w-48">Published</th>
                <th className="px-4 py-3 font-medium text-brand-700 w-48">Updated</th>
                <th className="px-4 py-3 font-medium text-brand-700 w-64 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-ink-line last:border-b-0 hover:bg-brand-100/30"
                  data-testid={`admin-blog-row-${p.id}`}
                >
                  <td className="px-4 py-4">
                    <div className="font-medium text-brand-700">{p.title}</div>
                    <div className="text-xs text-ink-muted mt-1">/{p.slug}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-[0.15em] ${
                        p.status === "published"
                          ? "bg-brand-700 text-white"
                          : "bg-brand-100 text-brand-700 border border-brand-200"
                      }`}
                      data-testid={`admin-blog-status-${p.id}`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-ink-soft">{formatDate(p.publishedAt)}</td>
                  <td className="px-4 py-4 text-ink-soft">{formatDate(p.updatedAt)}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {p.status === "published" && (
                        <a
                          href={`/blog/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-brand-700 px-2 py-1.5 transition-colors"
                          title="View on site"
                          data-testid={`admin-blog-view-${p.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => onEdit(p.id)}
                        className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:bg-brand-100 px-3 py-1.5 transition-colors border border-brand-700"
                        data-testid={`admin-blog-edit-${p.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      {confirmId === p.id ? (
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => deletePost(p.id)}
                            disabled={busyId === p.id}
                            className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60"
                            data-testid={`admin-blog-confirm-delete-${p.id}`}
                          >
                            {busyId === p.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                            Delete
                          </button>
                          <button
                            onClick={() => setConfirmId(null)}
                            className="text-sm text-ink-muted hover:text-brand-700 px-2 py-1.5"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmId(p.id)}
                          className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:bg-red-50 border border-red-200 px-3 py-1.5 transition-colors"
                          data-testid={`admin-blog-delete-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
